/**
 * Templates for the open workspace (TemplatesViewModel), provided by the app shell next to WorkspaceContext — only
 * where the `templates` flag is on. Absent → no gallery, no "Save as template" / "Duplicate" items, databases keep
 * the plain "New" button. Native: an `@Environment` value / `CompositionLocal` holding the same view model.
 */
import { RowTemplateViewModel, type RowTemplateState, type TemplatesState, type TemplatesViewModel } from "@bundle/core";
import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import { useWorkspaceContext } from "./AppContext.tsx";
import { useOwned } from "../hooks/owned.ts";

export const TemplatesContext = createContext<TemplatesViewModel | null>(null);

const noopSubscribe = () => () => {};
const none = (): null => null;

/** The workspace's templates view model and state (null when templates are off). */
export function useTemplates(): { vm: TemplatesViewModel; state: TemplatesState } | null {
  const vm = useContext(TemplatesContext);
  const state = useSyncExternalStore(vm?.subscribe ?? noopSubscribe, vm?.getSnapshot ?? none, vm?.getSnapshot ?? none);
  return vm === null || state === null ? null : { vm, state };
}

/** The templates view model without subscribing (null when off). */
export function useTemplatesViewModel(): TemplatesViewModel | null {
  return useContext(TemplatesContext);
}

type Box = { vm: RowTemplateViewModel | null; dispose: () => void };

/** The default values of one row template ("editing template" mode); null when templates are off. */
export function useRowTemplate(databaseId: string, templateId: string, readOnly: boolean): { vm: RowTemplateViewModel; state: RowTemplateState } | null {
  const templates = useContext(TemplatesContext);
  const { workspace } = useWorkspaceContext();
  const key = `rowtemplate:${workspace.wsId}:${workspace.state.meId}:${databaseId}:${templateId}:${readOnly ? "ro" : "rw"}:${templates === null ? "off" : "on"}`;
  const create = useCallback((): Box => {
    const vm = templates === null ? null : new RowTemplateViewModel(workspace, databaseId, templateId, templates.api, readOnly);
    return { vm, dispose: () => vm?.dispose() };
  }, [templates, workspace, databaseId, templateId, readOnly]);
  const vm = useOwned(key, create).vm;
  const state = useSyncExternalStore(vm?.subscribe ?? noopSubscribe, vm?.getSnapshot ?? none, vm?.getSnapshot ?? none);
  return vm === null || state === null ? null : { vm, state };
}
